import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import {
  GuaranteesBaseRelationsFieldName,
  GuaranteesBeneficiaryFieldName,
  GuaranteesFormFieldName,
  GuaranteeSecurityType,
} from '~/store/guarantees/types.ts';
import type {
  TGuaranteesBaseRelations,
  TGuaranteesBaseRelationsValues,
  TGuaranteesBeneficiary,
  TGuaranteesForm,
  TGuaranteesFormValues,
  TGuaranteesPrincipal,
  TGuaranteesPrincipalSection,
  TPatchGuaranteesBaseRelationsPayload,
  TPatchGuaranteesBeneficiaryPayload,
  TPatchGuaranteesFormPayload,
  TPatchGuaranteesPrincipalPayload,
} from '~/store/guarantees/types.ts';

export enum GuaranteesAccountTargetField {
  CoverageAccountIban = 'coverageAccountIban',
  DepositAccountIban = 'depositAccountIban',
  PrincipalDebitAccountIban = 'principalDebitAccountIban',
}

export type TGuaranteesAccountSecurityType =
  | GuaranteeSecurityType.COVERAGE_ACCOUNT
  | GuaranteeSecurityType.DEPOSIT_RIGHTS;

export enum GuaranteesPrincipalFieldName {
  PrincipalIdentifyCode = 'principal_identify_code',
  PrincipalFullName = 'principal_full_name',
  PrincipalShortName = 'principal_short_name',
  PrincipalLocation = 'principal_location',
  PrincipalNameAddressGuaranteeLang = 'principal_name_address_guarantee_lang',
  PrincipalContactFullName = 'principal_contact_full_name',
  PrincipalContactEmail = 'principal_contact_email',
  PrincipalContactPhone = 'principal_contact_phone',
  PrincipalAuthorizedPersonPosition = 'principal_authorized_person_position',
  PrincipalAuthorizedPersonName = 'principal_authorized_person_name',
  PrincipalHasChangesInConstituentDocuments = 'hasChangesInConstituentDocuments',
}

export enum GuaranteesPrincipalConstituentDocumentsChangesOption {
  YES = 'YES',
  NO = 'NO',
}

export { GuaranteesBaseRelationsFieldName, GuaranteesBeneficiaryFieldName, GuaranteesFormFieldName };

export type TGuaranteesPrincipalSectionSource =
  | (Partial<TGuaranteesPrincipal> & Record<string, unknown>)
  | (Record<string, unknown> & TGuaranteesPrincipalSection);

export interface IPrincipalSectionProps {
  principal: TGuaranteesPrincipalSectionSource | null;
  isLoading: boolean;
  isSaving: boolean;
  onSubmit: (payload: TPatchGuaranteesPrincipalPayload) => Promise<boolean>;
}

export interface IBeneficiarySectionProps {
  beneficiary: TGuaranteesBeneficiary | null;
  isLoading: boolean;
  isSaving: boolean;
  onSubmit: (payload: TPatchGuaranteesBeneficiaryPayload) => Promise<boolean>;
}

export interface IBaseRelationsSectionProps {
  baseRelations: TGuaranteesBaseRelations | null;
  isLoading: boolean;
  isSaving: boolean;
  isProzorroTender: boolean;
  onSubmit: (payload: TPatchGuaranteesBaseRelationsPayload) => Promise<boolean>;
}

export interface IGuaranteeFormSectionProps {
  guaranteeForm: TGuaranteesForm | null;
  isLoading: boolean;
  isSaving: boolean;
  onSubmit: (payload: TPatchGuaranteesFormPayload) => Promise<boolean>;
}

export type { TGuaranteesBaseRelationsValues, TGuaranteesFormValues };

export type TGuaranteesRootSectionFormValues = {
  guaranteeOperationType: TAutocompleteOption | null;
  guaranteeOperationTypeOther: string;
  guaranteeAmount: string;
  guaranteeCurrency: TAutocompleteOption | null;
  guaranteeExpiryDate: string;
  securityAmount: string;
  securityCurrencies: TAutocompleteOption[];
  requiredSecurityAmount: string;
  pledgorName: string;
  pledgorHasChangesInConstituentDocuments: TAutocompleteOption | null;
  pledgorIdentifyCode: string;
  isCounterpartyPrincipalDebitAccount: boolean;
  principalDebitAccount: TAutocompleteOption | null;
  principalDebitAccountIban: string;
  tenderSearch: string;
  mainDealId: string;
  mainDealNumber: string;
  mainDealDate: string;
  mainDealDateFrom: string;
  mainDealDateTo: string;
  mainDealCurrency: TAutocompleteOption | null;
  mainDealAmount: string;
  mainDealLimitBalance: string;
  checkAccountsRestrictions: boolean;
  checkAccountRestriction: boolean;
  isUrgent: boolean;
  securityType: TAutocompleteOption | null;
  isCounterpartyAccount: boolean;
  coverageAmount: string;
  depositAccountIban: string;
  coverageAccountIban: string;
  contract: TAutocompleteOption | null;
  account: TAutocompleteOption | null;
};

export type TGuaranteesSecretSectionSaveContext = {
  hasPledgorIdentifyCodeChanged: boolean;
};

export type TGuaranteesRootSectionFormErrors = Partial<
  Record<keyof TGuaranteesRootSectionFormValues, string>
>;

export type TGuaranteesContractOption = TAutocompleteOption & {
  mainDealId: string;
  mainDealNumber: string;
  mainDealDate: string;
  mainDealDateFrom: string;
  mainDealDateTo: string;
  mainDealCurrency: string;
  mainDealAmount: string;
  mainDealLimitBalance: string;
};

export type TGuaranteesAccountOption = TAutocompleteOption & {
  iban: string;
  targetField: GuaranteesAccountTargetField;
  accountType: string;
  frozen: boolean | null;
  ourBank: boolean | null;
};
